import { useEffect, useState } from 'react'
import { Helmet } from 'react-helmet'
import { Link, generatePath } from 'react-router-dom'
import PageTemplate from '../components/BaseComponents/PageTemplate/PageTemplate'
import PageHeadings from '../components/BaseComponents/PageHeadings/PageHeadings'
import { Button, InlineAlert } from '../components/BaseComponents'
import SimpleSortTable, {
	TableColumn,
} from '../components/BaseComponents/SimpleSortTable/SimpleSortTable'
import routes from '../components/Router/routes'
import useApi from '../hooks/useApi'
import { TEXT_LINK_FOCUS_RING } from '../lib/a11yFocus'
import ReviewUpgradeRequestModal, { ReviewableUser } from './AdminUsers/ReviewUpgradeRequestModal'


// Interim: replaced by the real admin dashboard when it is built (docs/todo/user-org-membership-i-fixes-B.md, B-4).
function AdminDashboard() {
	const api = useApi()
	const [loading, setLoading] = useState(true)
	const [error, setError] = useState(false)
	const [rows, setRows] = useState<any[]>([])
	const [reloadKey, setReloadKey] = useState(0)
	const [reviewingUser, setReviewingUser] = useState<ReviewableUser | null>(null)

	const columns: TableColumn[] = [
		{
			id: 'companyName',
			label: 'Organisation',
			renderer: (key, row) => (
				<Link
					className={`text-link ${TEXT_LINK_FOCUS_RING}`}
					to={generatePath(routes.adminCompany.path, { companyId: row.companyId })}
				>
					{row[key]}
				</Link>
			),
		},
		{
			id: 'userName',
			label: 'User',
			renderer: (key, row) => (
				<button
					type='button'
					className={`text-link ${TEXT_LINK_FOCUS_RING}`}
					onClick={() => setReviewingUser({ id: row.userId, fullName: row.userName, email: row.email })}
				>
					{row[key]}
				</button>
			),
		},
		{ id: 'email', label: 'Email' },
		{
			id: 'requestedAt',
			label: 'Requested',
			renderer: (key, row) => <span>{new Date(row[key]).toLocaleDateString('en-GB')}</span>,
		},
		{
			id: 'message',
			label: 'Message',
			renderer: (key, row) => <span>{row[key] ?? ''}</span>,
		},
	]

	useEffect(() => {
		let cancelled = false
		setLoading(true)
		setError(false)
		const run = async () => {
			try {
				const result = await api.get('admin/upgradeRequests')
				if (cancelled) return
				setRows(
					(result.data?.upgradeRequests ?? []).map((request: any) => ({
						...request,
						_id: request.userId,
					}))
				)
			} catch (_) {
				if (!cancelled) setError(true)
			} finally {
				if (!cancelled) setLoading(false)
			}
		}
		run()
		return () => {
			cancelled = true
		}
	}, [reloadKey, api])

	return (
		<PageTemplate testid='admin-dashboard-main'>
			<Helmet>
				<title>{routes.adminDashboard.name}</title>
			</Helmet>
			<PageHeadings title='Admin Dashboard' />
			<h3 className='text-white mb-3'>Upgrade requests</h3>
			{error ? (
				<InlineAlert type='error'>
					<p>Could not load upgrade requests. Please try again.</p>
					<Button type='Primary' text='Retry upgrade requests' onClick={() => setReloadKey((key) => key + 1)} />
				</InlineAlert>
			) : loading || rows.length > 0 ? (
				<SimpleSortTable isLoading={loading} columns={columns} data={rows} />
			) : (
				<p className='text-white'>No pending upgrade requests</p>
			)}
			<ReviewUpgradeRequestModal
				user={reviewingUser}
				onClose={() => setReviewingUser(null)}
				onReviewed={() => setReloadKey((key) => key + 1)}
			/>
		</PageTemplate>
	)
}
export default AdminDashboard
